import type { PlayMode } from './world.js';

// 球半径 [m]。engine/constants.ts の R と同値であることを tests/rack-seat.test.ts で検証する
// （ドリフト検知）。engine から import しないのは GPL 非依存維持（CLAUDE.md R-2）のため。
// world.ts の ballRadius も使えない（world.ts → rack-layout.ts の循環参照になり、
// 本ファイルはモジュール初期化時に R を使うため TDZ で落ちる）。
// なお engine の setR は本アプリでは未使用（実行時に R は変化しない）。
const R = 0.03275;
// テーブル長辺の半幅 [m]。engine/tablegeometry.ts の TableGeometry.X
// （= tableX + R = 43R + R = 44R）と同値（rack-seat.test.ts で検証）。
const TABLE_HALF_X = 44 * R;

/**
 * ラックレイアウト（自前・GPL非依存ロジック）。スロット（格子位置）ベース。
 *
 * - スロット = ラックの格子位置（決定論的タイト基準）。各スロットに既定番号がある。
 * - タブは「スロット→番号の並び（order）」と「スロット→位置オフセット（offsets）」を持つ。
 *   番号入替（order）と位置微調整（offsets）を分離して扱う。
 * - ロックスロット: 先頭(1番)と中央キー球(9/10/8)。入替・手動移動の対象外
 *   （公式ルール: 1番=先頭固定 / 9番=中央固定）。全体ナッジ操作でのみ動く。
 */

export interface RackSlot {
  readonly index: number;
  readonly x: number;
  readonly y: number;
  readonly defaultNumber: number;
  readonly locked: boolean;
}

/** スロット index → 位置オフセット [m]。 */
export type SlotOffsets = Record<number, { x: number; y: number }>;

export type RackableMode = Exclude<PlayMode, 'practice'>;

export interface RackTab {
  id: string;
  name: string;
  readonly: boolean;
  offsets: SlotOffsets;
  order: number[]; // order[slotIndex] = そのスロットに置く番号
}

export interface RackConfig {
  activeId: string;
  tabs: RackTab[];
}

export interface ResolvedBall {
  readonly number: number;
  readonly x: number;
  readonly y: number;
}

// ラックの基準隙間。実機の密ラックは球が接触する（中心間=2R）。
// 浮動小数の丸めで at-rest に意図せぬ衝突判定が出ない最小マージンだけ残した near-contact 値。
// 静止球は willCollide が運動を要求するため発火しないが、保険として極小の正値を採る。
// （旧値 0.2mm はラックが緩く、ブレークの伝達が不自然だったため 0.02mm へ）。
const TIGHT_EPS = 0.00002; // 0.02mm（near-contact）
const TIGHT_GAP = 2 * R + TIGHT_EPS;
export const RACK_OFFSET_LIMIT = R;
export const MAX_CUSTOM_TABS = 5;
export const BASIC_TAB_ID = 'basic';
const NUDGE_STEP = R * 0.1; // ナッジ1回の移動量（小さめ＝ちょんちょん）

const RACK_ROWS: Record<RackableMode, number[][]> = {
  nineBall: [[1], [2, 3], [4, 9, 5], [6, 7], [8]],
  tenBall: [[1], [2, 3], [4, 10, 5], [6, 7, 8, 9]],
  eightBall: [[1], [2, 9], [3, 8, 10], [4, 11, 12, 5], [7, 13, 6, 14, 15]],
};

/** 中央キー球（移動・入替ロック）。 */
const keyNumber = (mode: RackableMode): number =>
  mode === 'nineBall' ? 9 : mode === 'tenBall' ? 10 : 8;

const apexX = TABLE_HALF_X / 2;
const rowStepX = (TIGHT_GAP * Math.sqrt(3)) / 2;

/** モードのスロット定義（基準位置＋既定番号＋ロック）。 */
export const rackSlots = (mode: RackableMode): RackSlot[] => {
  const rows = RACK_ROWS[mode];
  const key = keyNumber(mode);
  const out: RackSlot[] = [];
  let index = 0;
  rows.forEach((ids, r) => {
    const x = apexX + r * rowStepX;
    const n = ids.length;
    ids.forEach((num, i) => {
      const y = (i - (n - 1) / 2) * TIGHT_GAP;
      out.push({
        index,
        x,
        y,
        defaultNumber: num,
        locked: num === 1 || num === key,
      });
      index++;
    });
  });
  return out;
};

export const defaultOrder = (mode: RackableMode): number[] =>
  rackSlots(mode).map((s) => s.defaultNumber);

const clampOff = (v: number): number =>
  Math.max(-RACK_OFFSET_LIMIT, Math.min(RACK_OFFSET_LIMIT, v));

/** タブを最終配置（番号＋座標）へ解決。 */
export const resolveRack = (mode: RackableMode, tab: RackTab): ResolvedBall[] => {
  const slots = rackSlots(mode);
  return slots.map((s) => {
    const o = tab.offsets[s.index];
    const num = tab.order[s.index] ?? s.defaultNumber;
    return {
      number: num,
      x: s.x + (o ? clampOff(o.x) : 0),
      y: s.y + (o ? clampOff(o.y) : 0),
    };
  });
};

/**
 * 重なり解消（保険）: 解決済み配置の球対で中心間 < 2R+eps のものを反復で押し離す。
 * 最も -x 側の球（apex=先頭球）を固定して残りを寄せる。
 *
 * 目的: ラックエディタのドラッグ残り・ジッタ等で初期配置に重なりが混入すると、
 * エンジン（接触点へ戻さず即時速度更新）が分離インパルスで球を弾き飛ばしたり、
 * 中心一致で法線が NaN 化して衝突解決ループが発散（depth>100）しフリーズする。
 * **エンジンへ渡る前に重なりをゼロ保証**するための純粋関数（自前・GPL非依存）。
 */
export const seatResolvedBalls = (balls: ResolvedBall[]): ResolvedBall[] => {
  const pos = balls.map((b) => ({ number: b.number, x: b.x, y: b.y }));
  if (pos.length < 2) return pos;
  // apex = 最も -x 側（先頭球）。これを固定アンカーにする。
  let anchor = 0;
  for (let i = 1; i < pos.length; i++) if (pos[i]!.x < pos[anchor]!.x) anchor = i;
  const min = 2 * R + TIGHT_EPS;
  // 反復（Gauss-Seidel 緩和）。長い連鎖でも収束するよう多めに回す（ラック生成は低頻度＝コスト無視可）。
  for (let it = 0; it < 200; it++) {
    let moved = false;
    for (let i = 0; i < pos.length; i++) {
      for (let j = i + 1; j < pos.length; j++) {
        const a = pos[i]!;
        const b = pos[j]!;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d = Math.hypot(dx, dy);
        if (d >= min) continue;
        if (d < 1e-6) {
          // 中心ほぼ一致（NaN 法線回避）: 任意方向（+x）へ最小分離。
          dx = TIGHT_EPS;
          dy = 0;
          d = TIGHT_EPS;
        }
        const push = (min - d) / d;
        const aFix = i === anchor;
        const bFix = j === anchor;
        const fa = aFix ? 0 : bFix ? 1 : 0.5;
        const fb = bFix ? 0 : aFix ? 1 : 0.5;
        a.x -= dx * push * fa;
        a.y -= dy * push * fa;
        b.x += dx * push * fb;
        b.y += dy * push * fb;
        moved = true;
      }
    }
    if (!moved) break;
  }
  return pos;
};

/** 隣接スロットのペア（基準位置で接触する組）。隙間可視化用。 */
export const neighborSlotPairs = (mode: RackableMode): Array<[number, number]> => {
  const slots = rackSlots(mode);
  const out: Array<[number, number]> = [];
  const thr = (2 * R * 1.18) ** 2;
  for (let i = 0; i < slots.length; i++) {
    for (let j = i + 1; j < slots.length; j++) {
      const dx = slots[i]!.x - slots[j]!.x;
      const dy = slots[i]!.y - slots[j]!.y;
      if (dx * dx + dy * dy <= thr) out.push([slots[i]!.index, slots[j]!.index]);
    }
  }
  return out;
};

// ---- 全体ナッジ操作（純粋関数） ----

const posFromOffsets = (
  mode: RackableMode,
  offsets: SlotOffsets,
): Map<number, { x: number; y: number }> => {
  const m = new Map<number, { x: number; y: number }>();
  for (const s of rackSlots(mode)) {
    const o = offsets[s.index];
    m.set(s.index, { x: s.x + (o?.x ?? 0), y: s.y + (o?.y ?? 0) });
  }
  return m;
};

const offsetsFromPos = (
  mode: RackableMode,
  pos: Map<number, { x: number; y: number }>,
): SlotOffsets => {
  const out: SlotOffsets = {};
  for (const s of rackSlots(mode)) {
    const p = pos.get(s.index)!;
    out[s.index] = { x: clampOff(p.x - s.x), y: clampOff(p.y - s.y) };
  }
  return out;
};

// apex（先頭球）スロット index。
const apexIndex = (mode: RackableMode): number =>
  rackSlots(mode).reduce((a, b) => (b.x < a.x ? b : a)).index;

// 重なり（中心間 < 2R）を反復で解消（apex 固定）。押し寄せた球を接触で止める。
const resolveOverlaps = (
  mode: RackableMode,
  pos: Map<number, { x: number; y: number }>,
): void => {
  const ids = [...pos.keys()];
  const anchor = apexIndex(mode);
  const min = 2 * R + TIGHT_EPS;
  for (let it = 0; it < 12; it++) {
    for (let i = 0; i < ids.length; i++) {
      for (let j = i + 1; j < ids.length; j++) {
        const a = pos.get(ids[i]!)!;
        const b = pos.get(ids[j]!)!;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d = Math.hypot(dx, dy);
        if (d >= min) continue;
        if (d < 1e-6) {
          dx = TIGHT_EPS;
          dy = 0;
          d = TIGHT_EPS;
        }
        const push = (min - d) / d;
        const aFix = ids[i] === anchor;
        const bFix = ids[j] === anchor;
        const fa = aFix ? 0 : bFix ? 1 : 0.5;
        const fb = bFix ? 0 : aFix ? 1 : 0.5;
        a.x -= dx * push * fa;
        a.y -= dy * push * fa;
        b.x += dx * push * fb;
        b.y += dy * push * fb;
      }
    }
  }
};

/** 下から押し上げ: apex 以外の全球を apex 方向(-x)へ寄せ、接触で密着させる（全球が影響）。 */
export const nudgeBack = (mode: RackableMode, offsets: SlotOffsets): SlotOffsets => {
  const pos = posFromOffsets(mode, offsets);
  const anchor = apexIndex(mode);
  for (const [idx, p] of pos) if (idx !== anchor) p.x -= NUDGE_STEP;
  resolveOverlaps(mode, pos);
  return offsetsFromPos(mode, pos);
};

// 1球だけを他球に重ならない位置へ押し戻す（その球のみ動かして接触で止める）。
const seatOne = (
  pos: Map<number, { x: number; y: number }>,
  idx: number,
): void => {
  const p = pos.get(idx)!;
  const min = 2 * R + TIGHT_EPS;
  for (let it = 0; it < 10; it++) {
    for (const [oid, q] of pos) {
      if (oid === idx) continue;
      let dx = p.x - q.x;
      let dy = p.y - q.y;
      let d = Math.hypot(dx, dy);
      if (d >= min) continue;
      if (d < 1e-6) {
        dx = min;
        dy = 0;
        d = min;
      }
      const push = (min - d) / d;
      p.x += dx * push;
      p.y += dy * push;
    }
  }
};

/** 上から押し下げ: 1番(apex)をパック方向(+x)へ寄せ、接触で止める（頭の隙間を詰める）。 */
export const nudgeApex = (mode: RackableMode, offsets: SlotOffsets): SlotOffsets => {
  const pos = posFromOffsets(mode, offsets);
  const idx = apexIndex(mode);
  pos.get(idx)!.x += NUDGE_STEP; // 「押し下げ」＝パック方向へ
  seatOne(pos, idx);
  return offsetsFromPos(mode, pos);
};

/** 横から挟む: 最も外側（|y|最大）の左右2球を中央へ寄せ、接触で密着させる。 */
export const nudgeSides = (mode: RackableMode, offsets: SlotOffsets): SlotOffsets => {
  const pos = posFromOffsets(mode, offsets);
  const slots = rackSlots(mode);
  const left = slots.filter((s) => s.y < 0).sort((a, b) => a.y - b.y)[0];
  const right = slots.filter((s) => s.y > 0).sort((a, b) => b.y - a.y)[0];
  if (left) pos.get(left.index)!.y += NUDGE_STEP;
  if (right) pos.get(right.index)!.y -= NUDGE_STEP;
  resolveOverlaps(mode, pos);
  return offsetsFromPos(mode, pos);
};

// ---- 番号入替 ----

/** スロット i, j の番号を入替（両方ロック解除時のみ）。order を破壊的更新して返す。 */
export const swapNumbers = (
  mode: RackableMode,
  order: number[],
  i: number,
  j: number,
): number[] => {
  const slots = rackSlots(mode);
  if (slots[i]?.locked || slots[j]?.locked || i === j) return order;
  const next = order.slice();
  const tmp = next[i]!;
  next[i] = next[j]!;
  next[j] = tmp;
  return next;
};

// ---- 永続化（localStorage・モード別・スロットモデル v2） ----
const keyFor = (mode: RackableMode): string => `pool.rackv2.${mode}`;

const sanitizeOffsets = (mode: RackableMode, raw: SlotOffsets): SlotOffsets => {
  const n = rackSlots(mode).length;
  const out: SlotOffsets = {};
  for (const [k, v] of Object.entries(raw)) {
    const idx = Number(k);
    if (!Number.isInteger(idx) || idx < 0 || idx >= n || !v) continue;
    if (Number.isFinite(v.x) && Number.isFinite(v.y)) {
      out[idx] = { x: clampOff(v.x), y: clampOff(v.y) };
    }
  }
  return out;
};

// order が既定番号の並べ替え（順列）として妥当か検証。違えば既定へ。
const sanitizeOrder = (mode: RackableMode, raw: number[]): number[] => {
  const def = defaultOrder(mode);
  if (!Array.isArray(raw) || raw.length !== def.length) return def;
  const sortedRaw = [...raw].sort((a, b) => a - b);
  const sortedDef = [...def].sort((a, b) => a - b);
  if (sortedRaw.some((v, i) => v !== sortedDef[i])) return def;
  return raw.slice();
};

export const basicTab = (mode: RackableMode): RackTab => ({
  id: BASIC_TAB_ID,
  name: 'ベーシック',
  readonly: true,
  offsets: {},
  order: defaultOrder(mode),
});

const defaultConfig = (mode: RackableMode): RackConfig => ({
  activeId: BASIC_TAB_ID,
  tabs: [basicTab(mode)],
});

export const loadRackConfig = (mode: RackableMode): RackConfig => {
  try {
    const raw = localStorage.getItem(keyFor(mode));
    if (!raw) return defaultConfig(mode);
    const parsed = JSON.parse(raw) as RackConfig;
    if (!Array.isArray(parsed.tabs)) return defaultConfig(mode);
    const tabs: RackTab[] = [basicTab(mode)];
    for (const t of parsed.tabs) {
      if (!t || t.id === BASIC_TAB_ID) continue;
      if (typeof t.id !== 'string' || typeof t.name !== 'string') continue;
      if (tabs.length > MAX_CUSTOM_TABS) break;
      tabs.push({
        id: t.id,
        name: t.name,
        readonly: false,
        offsets: sanitizeOffsets(mode, t.offsets ?? {}),
        order: sanitizeOrder(mode, t.order ?? []),
      });
    }
    const activeId = tabs.some((t) => t.id === parsed.activeId)
      ? parsed.activeId
      : BASIC_TAB_ID;
    return { activeId, tabs };
  } catch (err) {
    console.error('rackConfig: 読み込み失敗:', err);
    return defaultConfig(mode);
  }
};

export const saveRackConfig = (mode: RackableMode, config: RackConfig): void => {
  try {
    localStorage.setItem(keyFor(mode), JSON.stringify(config));
  } catch (err) {
    console.error('rackConfig: 保存失敗:', err);
  }
};

/** ゲーム適用に使う、アクティブタブの解決済み配置。 */
export const activeResolved = (mode: RackableMode): ResolvedBall[] => {
  const cfg = loadRackConfig(mode);
  const tab = cfg.tabs.find((t) => t.id === cfg.activeId) ?? cfg.tabs[0]!;
  return resolveRack(mode, tab);
};

export const nextTabId = (cfg: RackConfig): string => {
  let n = cfg.tabs.length;
  let id = `c${n}`;
  while (cfg.tabs.some((t) => t.id === id)) id = `c${++n}`;
  return id;
};
